import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { TabletButton } from '@campfire/tablet-button';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';

const DEFAULT_TAG_COLOUR = '#796fff';

export type BulkTagMode = 'add' | 'remove';

interface TagOption {
  volunteerTagId: string;
  name: string;
  colour: string | null;
  tagGroup: string | null;
}

interface Props {
  open: boolean;
  mode: BulkTagMode;
  onClose: () => void;
  volunteerIds: string[];
  tags: TagOption[];
  onSuccess: () => void;
}

export const BulkTagDialog: React.FC<Props> = ({ open, mode, onClose, volunteerIds, tags, onSuccess }) => {
  const { theme } = useCampfireTheme();
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const { run } = useCampfireFetch({ defer: true });

  const isAdd = mode === 'add';

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]));
  };

  const handleClose = () => {
    setSelectedTagIds([]);
    onClose();
  };

  const handleSubmit = () => {
    setSubmitting(true);
    run({
      url: `${apiUrl}/vm/volunteer-tags/${isAdd ? 'add-bulk' : 'remove-bulk'}`,
      method: 'post',
      data: { volunteerTagIds: selectedTagIds, volunteerIds },
    })
      .then((res: any) => {
        if (!res?.ok) {
          setSnackbar({ variant: 'error', message: `Unable to ${mode} tags`, open: true });
          return;
        }
        setSnackbar({
          variant: 'success',
          message: isAdd ? 'Tags added' : 'Tags removed',
          open: true,
        });
        setSelectedTagIds([]);
        onSuccess();
        onClose();
      })
      .catch(() => setSnackbar({ variant: 'error', message: `Unable to ${mode} tags`, open: true }))
      .finally(() => setSubmitting(false));
  };

  const orderedTags = [...tags].sort((a, b) => {
    const groupCompare = (a.tagGroup || '￿').localeCompare(b.tagGroup || '￿');
    return groupCompare !== 0 ? groupCompare : a.name.localeCompare(b.name);
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>{isAdd ? 'Add Tags' : 'Remove Tags'}</DialogTitle>
      <DialogContent style={{ padding: 0 }}>
        <Box paddingX={3} paddingBottom={1}>
          <Typography style={{ fontSize: 14, color: '#808080' }}>
            {isAdd
              ? `Adds the tags you pick to all ${volunteerIds.length} selected volunteers. Anyone who already has a tag keeps it unchanged.`
              : `Removes the tags you pick from all ${volunteerIds.length} selected volunteers. Anyone without a tag is skipped.`}
          </Typography>
        </Box>
        {orderedTags.length === 0 ? (
          <Box padding={3}>
            <Typography color='textSecondary'>No user tags available.</Typography>
          </Box>
        ) : (
          <List style={{ paddingBottom: 0 }}>
            {orderedTags.map((tag) => (
              <ListItem
                key={tag.volunteerTagId}
                button
                onClick={() => toggleTag(tag.volunteerTagId)}
                style={{ borderBottom: `1px solid ${theme.color.grey.border}`, padding: '12px 24px' }}
              >
                <ListItemIcon style={{ minWidth: 40 }}>
                  <Checkbox
                    checked={selectedTagIds.includes(tag.volunteerTagId)}
                    edge='start'
                    disableRipple
                    color='primary'
                  />
                </ListItemIcon>
                <ListItemIcon style={{ minWidth: 24 }}>
                  <Box
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: tag.colour || DEFAULT_TAG_COLOUR,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography style={{ fontWeight: 600, fontSize: 15, color: '#444' }}>{tag.name}</Typography>}
                  secondary={
                    tag.tagGroup ? (
                      <Typography style={{ fontSize: 12, color: '#9E9E9E' }}>{tag.tagGroup}</Typography>
                    ) : null
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Box padding={1} display='flex' justifyContent='flex-end' width='100%'>
          <TabletButton variant='text' onClick={handleClose} disabled={submitting} style={{ marginRight: 8 }}>
            Cancel
          </TabletButton>
          <TabletButton
            variant='contained'
            color='primary'
            onClick={handleSubmit}
            disabled={selectedTagIds.length === 0 || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : isAdd ? 'Add Tags' : 'Remove Tags'}
          </TabletButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
